import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { PageHeader } from '@/components/patterns';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Box, MapPin, Calendar } from 'lucide-react';
import { ContainerDetailPanel } from '../components/container-detail-panel';
import { FreightImpactPanel } from '../components/freight-impact-panel';
import { prorateAcrossContainers } from '../utils/container-freight-proration';
import { motivoParaNoEditarArmado } from '../utils/order-state-rules';
import { ContainerStatusBadge } from '../components/container-status-badge';
import { useImportTrackingStore } from '../store/import-tracking-store';

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-BO', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export function ContainerDetailPage() {
  const { orderId, containerId } = useParams<{ orderId: string; containerId: string }>();
  const navigate = useNavigate();

  const {
    currentContainer,
    containerItems,
    containerTypes,
    containers,
    isLoadingContainers,
    isLoadingContainerItems,
    fetchContainer,
    fetchContainerItems,
    fetchContainerTypes,
    fetchOrderContainers,
    currentOrder,
    fetchOrder,
  } = useImportTrackingStore();

  useEffect(() => {
    if (containerId) {
      fetchContainer(containerId);
      fetchContainerItems(containerId);
    }
    if (containerTypes.length === 0) {
      fetchContainerTypes();
    }
    if (orderId) {
      if (!currentOrder) fetchOrder(orderId);
      // Los hermanos hacen falta para repartir el flete: sin saber cuántos
      // contenedores lleva la orden, este se llevaría el flete completo.
      fetchOrderContainers(orderId);
    }
  }, [containerId, orderId]);

  const handleRefresh = () => {
    if (containerId) {
      fetchContainer(containerId);
      fetchContainerItems(containerId);
    }
  };

  /*
    ⚠ Todos los hooks van ACÁ, antes de cualquier `return` temprano.

    React exige que la cantidad y el orden de hooks sea igual en cada render. Si
    un `useMemo` queda después del `if (isLoading) return …`, el primer render
    ejecuta cero y el segundo dos, y React tira "Rendered more hooks than during
    the previous render". Pasó al escribir esto y por eso queda anotado.

    Por eso los memos toleran que `currentContainer` sea null en vez de asumir
    que ya llegó.
  */

  /**
   * El flete que le corresponde a ESTE contenedor.
   *
   * Dos niveles: primero el flete del embarque se reparte entre los contenedores
   * de la orden, y recién después ese pedazo se reparte entre los productos.
   *
   * Antes se pasaba el `totalFreight` completo a cada contenedor, así que una
   * orden con dos daba el costo por unidad **al doble**. Y es el caso normal: 520
   * productos no entran en un 40HQ.
   */
  const orderFreight = Number(currentOrder?.totalFreight ?? 0);

  /** Los contenedores de la orden, con lo que hace falta para el reparto. */
  const siblings = useMemo(() => {
    // Si todavía no llegaron los hermanos, se asume que este es el único: es lo
    // conservador — reparte todo acá hasta saber que hay más.
    const list = containers.length > 0 ? containers : currentContainer ? [currentContainer] : [];
    return list.map((c) => ({
      id: c.id,
      label: c.containerNumber || 'Contenedor',
      occupiedVolumeCbm: c.usedVolumeCbm > 0 ? c.usedVolumeCbm : null,
      capacityCbm:
        c.containerType?.maxVolumeCbm ??
        containerTypes.find((ct) => ct.id === c.containerTypeId)?.maxVolumeCbm ??
        null,
    }));
  }, [containers, currentContainer, containerTypes]);

  const containerSplit = useMemo(
    () => prorateAcrossContainers({ freightTotal: orderFreight, containers: siblings }),
    [orderFreight, siblings]
  );

  /**
   * Los ítems del contenedor en la forma que necesita el prorrateo.
   *
   * `unitFobValue` va en 0 porque `ContainerItem` no lo guarda — el FOB vive en
   * `ImportOrderItem`. El panel detecta que falta y deshabilita el reparto por
   * valor en vez de mostrar partes iguales como si fuera el resultado.
   */
  const prorationItems = useMemo(
    () =>
      containerItems.map((item) => ({
        id: item.id,
        description: item.material?.name ?? item.description,
        quantity: item.quantity,
        // cm³ → m³
        unitVolumeCbm: (item.lengthCm * item.widthCm * item.heightCm) / 1_000_000,
        unitWeightKg: item.weightKg,
        unitFobValue: 0,
      })),
    [containerItems]
  );

  // Loading state
  if (isLoadingContainers || isLoadingContainerItems) {
    return (
      <AppLayout>
        <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32 w-full rounded-lg" />
          <Skeleton className="h-96 w-full rounded-lg" />
        </div>
      </AppLayout>
    );
  }

  // Not found
  if (!currentContainer) {
    return (
      <AppLayout>
        <div className="mx-auto max-w-[1440px] px-4 py-16 text-center md:px-8">
          <p className="text-muted-foreground">Contenedor no encontrado</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate(orderId ? `/trade/embarques/${orderId}` : '/trade/embarques')}
          >
            Volver
          </Button>
        </div>
      </AppLayout>
    );
  }

  const container = currentContainer;
  const containerType =
    container.containerType || containerTypes.find((ct) => ct.id === container.containerTypeId);

  /** Lo que le toca a este contenedor del flete del embarque. */
  const myFreight =
    containerSplit.containers.find((c) => c.id === container.id)?.freightAmount ?? orderFreight;

  if (!containerType) {
    return (
      <AppLayout>
        <div className="mx-auto max-w-[1440px] px-4 py-16 text-center md:px-8">
          <p className="text-muted-foreground">Tipo de contenedor no encontrado</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate(orderId ? `/trade/embarques/${orderId}` : '/trade/embarques')}
          >
            Volver
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-4 py-8 md:px-8">
        {/*
          El encabezado de Comercia (artefacto, 19-oct). Las migas propias que
          había acá («Import/Export / orden / Contenedor») repetían las de arriba;
          la vuelta a la orden queda como «volver».
        */}
        <PageHeader
          volver={{
            texto: currentOrder ? `Volver a ${currentOrder.orderNumber}` : 'Volver a Import/Export',
            onClick: () => navigate(orderId ? `/trade/embarques/${orderId}` : '/trade/embarques'),
          }}
          modulo="comercia"
          etiqueta="Comercia · Carga"
          icono={Box}
          titulo={
            <span className="inline-flex flex-wrap items-center gap-3">
              {container.containerNumber || 'Contenedor sin número'}
              <ContainerStatusBadge status={container.status} />
            </span>
          }
          subtitulo={`${containerType.name} (${containerType.code}) · ${containerType.lengthCm} × ${containerType.widthCm} × ${containerType.heightCm} cm`}
        />

        {/* Container Metadata */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {container.originPort && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground text-xs">Puerto Origen</p>
                    <p className="font-medium">{container.originPort}</p>
                  </div>
                </div>
              )}
              {container.destinationPort && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground text-xs">Puerto Destino</p>
                    <p className="font-medium">{container.destinationPort}</p>
                  </div>
                </div>
              )}
              {container.etd && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground text-xs">ETD</p>
                    <p className="font-medium">{formatDate(container.etd)}</p>
                  </div>
                </div>
              )}
              {container.eta && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground text-xs">ETA</p>
                    <p className="font-medium">{formatDate(container.eta)}</p>
                  </div>
                </div>
              )}
              {container.sealNumber && (
                <div>
                  <p className="text-muted-foreground text-xs">N. Sello</p>
                  <p className="font-medium">{container.sealNumber}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 3D Panel */}
        <ContainerDetailPanel
          container={container}
          containerType={containerType}
          items={containerItems}
          onRefresh={handleRefresh}
          /*
            El armado de una carga ya entregada no se reescribe.

            🔴 Antes no había freno en ninguna capa: se podía entrar acá con la
            orden entregada, mover bultos y guardar. Si después alguien audita
            qué venía en ese contenedor, el sistema mostraría un armado que
            nunca existió. El backend ahora también lo rechaza — esto es para
            no ofrecer algo que va a fallar.
          */
          motivoSoloLectura={currentOrder ? motivoParaNoEditarArmado(currentOrder.status) : null}
        />

        {/*
          El cierre del círculo: la ocupación real reparte el flete y ese reparto
          define el costo por unidad. Va DEBAJO del 3D a propósito — primero se
          arma, después se ve qué costó.

          El volumen ocupado sale del contenedor guardado (`usedVolumeCbm`), que
          es lo que quedó tras el último "Guardar posiciones". Si nunca se armó,
          es null y el prorrateo cae al volumen declarado, avisando.
        */}
        <FreightImpactPanel
          freightTotal={myFreight}
          items={prorationItems}
          occupiedVolumeCbm={container.usedVolumeCbm > 0 ? container.usedVolumeCbm : null}
          containerVolumeCbm={containerType?.maxVolumeCbm ?? null}
          containerSplit={
            // Solo se explica el reparto si hay más de uno. Con un contenedor
            // solo, decir "le corresponden 10.440 de 10.440" es ruido.
            containerSplit.containers.length > 1
              ? {
                  orderFreight,
                  position: containerSplit.containers.findIndex((c) => c.id === container.id) + 1,
                  total: containerSplit.containers.length,
                  basis: containerSplit.basis,
                }
              : undefined
          }
        />
      </div>
    </AppLayout>
  );
}

export default ContainerDetailPage;
